import { ref } from 'vue'
import type { CommonBlok } from '~/types/storyblok'

export interface VideoView {
  view_type: 'view_video',
  /**
   * The title of the video.
   */
  label: string,
}

export interface VideoInteraction {
  /**
   * Percentage of the current playback position when the interaction happens.
   * 
   * @example '10%'
   */
  video_percent: string
  video_progress: 'play' | 'pause' | 'end' | ''
  video_title: string
}


let useTrackingClassInstance: Tracking | null = null
const etData: Ref<EtData | null> = ref(null)

const isCommonBlok = (blok: unknown): blok is CommonBlok => {
  return !!blok && typeof blok === 'object' && '_uid' in blok
}

export default function useTracking() {
  const init = (gtmKey: string, initData: Record<string, TrackingDataEntry> = {}) => {
    if (window && document && gtmKey) {
      useTrackingClassInstance ??= new Tracking()
      useTrackingClassInstance.initGoogleTagManager(gtmKey)
      useTrackingClassInstance.trackDatalayer(initData)
    }
  }

  const initET = async (productName: string, versionNumber: string|null = null) => {
    useTrackingClassInstance ??= new Tracking()
    await useTrackingClassInstance.initET(
      productName,
      versionNumber,
      (await usePageConfig()).etUrl,
    )
    etData.value = useTrackingClassInstance?.etData
  }

  const trackDatalayer = (data: Record<string, TrackingDataEntry>) => {
    if (useTrackingClassInstance) {
      useTrackingClassInstance?.trackDatalayer(data)
    }
  }

  const trackClick = (referenceBlok: CommonBlok, trackComponentName: string, trackingLabel: string, mainComponent?: string): void => {
    if (!mainComponent?.trim()) {
      const story = useStoryData()
      const sections = story.storyData?.content.ContentBlocks?.filter((blok) => blok.component.includes('Section')) ?? []

      const matchedSection = sections.find((section) => pathInTree(section, (element) => isCommonBlok(element) && element._uid === referenceBlok._uid) !== null)
      if (matchedSection) {
        mainComponent = matchedSection.component
        if ('intro' in matchedSection && (matchedSection.intro ?? []).length > 0) {
          mainComponent = `${mainComponent}_${matchedSection.intro![0].heading}`
        } else if ('heading' in matchedSection) {
          mainComponent = `${mainComponent}_${matchedSection.heading}`
        }
      }
    }

    const trackingStore = useGaTrackingStore()

    useTrackingClassInstance?.trackDatalayer({
      event: 'gaEvent',
      event_name: 'click',
      click: {
        ...trackingStore.pageTrackingData,
        component: trackComponentName,
        main_component: mainComponent,
        label: trackingLabel,
      },
    })
  }

  const trackVideo = (data: VideoInteraction) => {
    const trackingStore = useGaTrackingStore()

    const eventData = {
      event: 'gaEvent',
      event_name: 'video_interaction',
      video_interaction: {
        ...trackingStore.pageTrackingData,
        // Cleanup data and remove other data that might be on the object
        video_percent: data.video_percent,
        video_progress: data.video_progress,
        video_title: data.video_title,
      },
    }
    useTrackingClassInstance?.trackDatalayer(eventData)
  }

  const trackView = (data: VideoView) => {
    const trackingStore = useGaTrackingStore()

    const eventData = {
      event: 'gaEvent',
      event_name: 'view',
      view: {
        ...trackingStore.pageTrackingData,
        // Cleanup data and remove other data that might be on the object
        view_type: data.view_type,
        label: data.label,
      },
    }
    useTrackingClassInstance?.trackDatalayer(eventData)
  }

  const isExternalTrackingActive = (): boolean => {
    if (import.meta.server) {
      return false
    }

    return window.dataLayer?.find((layer) => layer.disableTracking) === undefined
  }

  /**
   * Disables external tracking added through GoogleAnalytics.
   */
  const disableExternalTracking = (): void => {
    const shouldReload = isExternalTrackingActive()

    if (import.meta.server) {
      useHtml().writeScript(`
        if (!window.dataLayer) window.dataLayer = [];
        window.dataLayer.push({ event: 'disableTracking', disableTracking: true })
      `)
    } else {
      trackDatalayer({
        event: 'disableTracking',
        disableTracking: true, // a custom made switch inside Google Analytics.
      })
    }

    
    // reload app/ trigger a hard reload (to enable tracking), if the user leaves
    onBeforeRouteUpdate((guard) => {
      // TODO: it seams that this type don't have `href` as property
      // @ts-expect-error -- We need to check if this is a bug
      void navigateTo(guard.href, { external: true })
    })
  

    if (shouldReload) {
      reloadNuxtApp({ persistState: false })
    }
  }

  return {
    init,
    initET,
    trackDatalayer,
    trackClick,
    etData,
    trackVideo,
    trackView,
    isExternalTrackingActive,
    disableExternalTracking,
  }
}
